import React from 'react';
import {
  AIEmailWriterForm,
  Name,
  BusinessName,
  BusinessType,
  AddressToDistrictField,
  OptionGroup,
  Option,
  CustomNotes,
  FormRow,
  FormSection,
  FormColumn,
} from './ai-email-writer';

export const BusinessZoningForm = () => (
  <AIEmailWriterForm
    campaignId="family-zoning-business"
    campaignContext="Mayor Lurie's Family Zoning Plan would open single-family zones to duplexes, triplexes, and small apartments, prioritizing family-sized units to keep working families in San Francisco. This directly benefits businesses by ensuring a stable workforce, expanding customer bases, and supporting vibrant local economies. When employees can afford to live near where they work, businesses benefit from reduced turnover, shorter commutes, and a more engaged workforce. The plan promotes economic diversity and community stability that small businesses depend on."
    systemPrompt="You are helping a San Francisco business owner write a persuasive, professional email to city officials. The email should be:
- Professional but personal, reflecting their experience as a business owner
- Concise and short, but compelling (around 150 words)
- Focused on how housing affects their business, employees, and customers
- Include specific business reasons for supporting the policy
- Connect housing policy to economic vitality and workforce needs
- Do not include a subject line at the beginning of the response
- Maintain a respectful, constructive tone
- Emphasize community partnership and economic benefits"
    recipient="San Francisco Planning Commission"
    recipientEmail="lydia.so@sfgov.org,kathrin.moore@sfgov.org,derek.braun@sfgov.org,amy.campbell@sfgov.org,theresa.imperial@sfgov.org,sean.mcgarry@sfgov.org,gilbert.a.williams@sfgov.org"
    ccEmail="commissions.secretary@sfgov.org,jonas.ionin@sfgov.org,act+family-zoning-business@growsf.org"
    subject="Business Support for Mayor Lurie's Family Zoning Plan"
  >
    <FormSection>
      <FormRow>
        <Name required />
        <BusinessName />
      </FormRow>
      <AddressToDistrictField label="Your Business's Address" />

      <FormRow>
        <BusinessType />
      </FormRow>

      <FormRow>
        <FormColumn>
          <OptionGroup
            id="businessImpact"
            type="checkbox"
            label="How does housing affect your business?"
          >
            <Option
              value="employees"
              label="Employ SF residents"
              render="I employ San Francisco residents"
            />
            <Option
              value="customer-base"
              label="Serve local families"
              render="my business serves local families"
            />
            <Option
              value="workforce-housing"
              label="Need workforce housing"
              render="my employees need affordable housing options"
            />
            <Option
              value="recruitment-challenges"
              label="Struggle to recruit talent"
              render="I struggle to recruit quality talent due to housing costs"
            />
            <Option
              value="employee-commutes"
              label="Employees have long commutes"
              render="many of my employees commute long distances because they can't afford to live in the city"
            />
          </OptionGroup>
        </FormColumn>

        <FormColumn>
          <OptionGroup
            id="businessChallenges"
            type="checkbox"
            label="What housing-related challenges do you face?"
          >
            <Option
              value="high-turnover"
              label="High employee turnover"
              render="I experience high employee turnover due to housing displacement"
            />
            <Option
              value="reduced-foot-traffic"
              label="Declining foot traffic"
              render="I've noticed declining foot traffic as families move away"
            />
            <Option
              value="limited-customer-base"
              label="Shrinking customer base"
              render="my customer base is shrinking as working families leave the neighborhood"
            />
            <Option
              value="payroll-pressure"
              label="Pressure to raise wages & prices"
              render="I face constant pressure to raise wages, and therefore prices, to help employees afford housing"
            />
          </OptionGroup>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <OptionGroup id="businessSize" type="radio" label="Business size">
            <Option value="solo" label="Solo entrepreneur" render="I am a solo entrepreneur" />
            <Option
              value="small-team"
              label="2-10 employees"
              render="I run a small business with a team of employees"
            />
            <Option
              value="medium"
              label="11-50 employees"
              render="I operate a medium-sized business"
            />
            <Option
              value="large"
              label="50+ employees"
              render="I run a larger business that employs many San Francisco residents"
            />
          </OptionGroup>
        </FormColumn>

        <FormColumn>
          <OptionGroup
            id="priorities"
            type="checkbox"
            label="What business values drive your support?"
            formatMultiple={(items) => `As a business owner, I value ${items.join(', ')}`}
          >
            <Option
              value="economic-growth"
              label="Economic growth"
              render="economic growth and business expansion"
            />
            <Option
              value="community-support"
              label="Community investment"
              render="investing in our community's long-term health"
            />
            <Option
              value="sustainability"
              label="Business sustainability"
              render="creating sustainable business conditions"
            />
            <Option
              value="workforce-stability"
              label="Workforce stability"
              render="maintaining a stable, local workforce"
            />
            <Option
              value="neighborhood-vitality"
              label="Neighborhood vitality"
              render="preserving the vitality of our neighborhood"
            />
          </OptionGroup>
        </FormColumn>
      </FormRow>

      <CustomNotes />
    </FormSection>
  </AIEmailWriterForm>
);